import { atom, useAtom, useAtomValue } from "jotai"
import { useEffect, useId, useRef, useState } from "react"

const openLayerIdsAtom = atom<string[]>([])

export const LAYER_Z_INDEX = {
    BASE: 1000,
    STEP: 10,
    OVERLAY_OFFSET: 0,
    SURFACE_OFFSET: 1,
    FLOATING_OFFSET: 2,
}

interface UseLayerStackOptions {
    /**
     * If > 0, the layer stays registered in the stack for this many ms after `isOpen`
     * becomes false. Useful for exit animations (e.g. BottomDrawer slide-out) so the
     * z-index doesn't collapse before the animation completes.
     */
    closeDelay?: number
}

interface UseLayerStackReturn {
    layerIndex: number
    /** True while the layer is open OR during the closeDelay exit animation. */
    isStackActive: boolean
    overlayZIndex: number
    surfaceZIndex: number
    floatingZIndex: number
}

/**
 * The z-index for transient chrome (tooltips, hints) that has to sit above every open layer,
 * including the popover or drawer it was opened from.
 */
export function useAboveLayersZIndex(): number {
    const openLayerIds = useAtomValue(openLayerIdsAtom)

    return LAYER_Z_INDEX.BASE + openLayerIds.length * LAYER_Z_INDEX.STEP + LAYER_Z_INDEX.FLOATING_OFFSET
}

export default function useLayerStack(isOpen: boolean, options?: UseLayerStackOptions): UseLayerStackReturn {
    const { closeDelay } = options ?? {}
    const [openLayerIds, setOpenLayerIds] = useAtom(openLayerIdsAtom)
    const layerId = useId()
    const hasRegisteredRef = useRef(false)

    const [isStackActive, setIsStackActive] = useState(isOpen)

    useEffect(() => {
        if (isOpen) {
            setIsStackActive(true)
            return
        }

        if (!isStackActive) {
            return
        }

        if (!closeDelay) {
            setIsStackActive(false)
            return
        }

        const timeoutId = window.setTimeout(() => {
            setIsStackActive(false)
        }, closeDelay)

        return () => {
            window.clearTimeout(timeoutId)
        }
    }, [isOpen, isStackActive, closeDelay])

    useEffect(() => {
        if (isStackActive && !hasRegisteredRef.current) {
            hasRegisteredRef.current = true
            setOpenLayerIds((previousLayerIds) => {
                if (previousLayerIds.includes(layerId)) {
                    return previousLayerIds
                }

                return [...previousLayerIds, layerId]
            })
            return
        }

        if (!isStackActive && hasRegisteredRef.current) {
            hasRegisteredRef.current = false
            setOpenLayerIds((previousLayerIds) => previousLayerIds.filter((id) => id !== layerId))
        }
    }, [isStackActive, layerId, setOpenLayerIds])

    useEffect(() => {
        return () => {
            if (!hasRegisteredRef.current) {
                return
            }

            hasRegisteredRef.current = false
            setOpenLayerIds((previousLayerIds) => previousLayerIds.filter((id) => id !== layerId))
        }
    }, [layerId, setOpenLayerIds])

    const currentLayerIndex = openLayerIds.indexOf(layerId)
    const layerIndex =
        isStackActive || isOpen
            ? currentLayerIndex === -1
                ? openLayerIds.length
                : currentLayerIndex
            : Math.max(currentLayerIndex, 0)

    const overlayZIndex = LAYER_Z_INDEX.BASE + layerIndex * LAYER_Z_INDEX.STEP + LAYER_Z_INDEX.OVERLAY_OFFSET
    const surfaceZIndex = LAYER_Z_INDEX.BASE + layerIndex * LAYER_Z_INDEX.STEP + LAYER_Z_INDEX.SURFACE_OFFSET
    const floatingZIndex = LAYER_Z_INDEX.BASE + layerIndex * LAYER_Z_INDEX.STEP + LAYER_Z_INDEX.FLOATING_OFFSET

    return {
        layerIndex,
        isStackActive,
        overlayZIndex,
        surfaceZIndex,
        floatingZIndex,
    }
}
